import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import client from "FrontendJsAssets/common/client";

const modal = document.getElementById("ean-scanner");
let codeReader = null;
let blurAnimationId = null;
let videoStream = null;
let scanningTimeoutId = null;
let isScanning = false;
let lastScannedCode = null;

// "Result mode" pre natívny scanner v appke (camera-scanner-app.js):
// modal sa otvorí len ako message overlay, kamera/ZXing sa nespúšťa.
let nativeResultMode = false;
let nativeRetryHandler = null;

if (modal) {
    // Close button handler
    const closeBtn = modal.querySelector(".sc-close");
    if (closeBtn) {
        closeBtn.addEventListener("click", () => {
            $(modal).modal("hide");
        });
    }

    // Flashlight button handler
    const flashlightBtn = modal.querySelector(".sc-flashlight");
    let flashlightOn = false;

    if (flashlightBtn) {
        flashlightBtn.addEventListener("click", async () => {
            if (!videoStream) {
                console.warn("Stream not ready");
                return;
            }

            const track = videoStream.getVideoTracks()[0];
            if (!track) {
                console.warn("No video track");
                return;
            }

            const capabilities = track.getCapabilities();

            if (!capabilities.torch) {
                console.warn("Flashlight not supported on this device");
                return;
            }

            try {
                flashlightOn = !flashlightOn;
                await track.applyConstraints({
                    advanced: [{ torch: flashlightOn }],
                });

                // Toggle button visual state
                flashlightBtn.classList.toggle("is-active", flashlightOn);
            } catch (error) {
                console.error("Failed to toggle flashlight", error);
            }
        });
    }

    // Retry button handler
    const retryBtn = modal.querySelector(".sc-message__retry");
    if (retryBtn) {
        retryBtn.addEventListener("click", () => {
            // V result mode retry znovu spúšťa natívny scanner v appke
            if (nativeResultMode && nativeRetryHandler) {
                nativeRetryHandler();
                return;
            }

            // Reset scanning state
            isScanning = false;
            lastScannedCode = null;

            // Hide message
            const messageEl = modal.querySelector(".js-ean-scanner-message");
            if (messageEl) {
                messageEl.classList.remove("is-loading", "is-success", "is-error");
                messageEl.classList.add("is-hidden");
                const textEl = messageEl.querySelector(".sc-message__text");
                if (textEl) textEl.textContent = "";
            }
        });
    }

    $(modal).on("shown.bs.modal", async () => {
        // Reset scanning state when modal opens
        isScanning = false;
        lastScannedCode = null;

        // Result mode (natívny sken v appke): žiadna kamera, len message overlay
        if (nativeResultMode) {
            const loadingEl = modal.querySelector(".js-scanner-loading");
            if (loadingEl) {
                loadingEl.classList.add("is-hidden");
            }
            return;
        }

        try {
            const videoEl = document.getElementById("reader");
            const canvas = document.getElementById("scanner-canvas");

            if (!videoEl) {
                console.error("Video element not found");
                return;
            }

            // Stop any previous instance
            if (codeReader) {
                try {
                    codeReader.reset();
                } catch (_) {}
            }

            // Enumerate devices and get cameras
            const devices = await navigator.mediaDevices.enumerateDevices();
            const cameras = devices.filter((device) => device.kind === "videoinput");

            if (cameras.length === 0) {
                throw new Error("No camera found on this device.");
            }

            // Select back camera (usually the last one)
            const camera = cameras[cameras.length - 1];

            // Create stream manually with getUserMedia with optimized settings
            // Lower resolution for faster barcode processing
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    deviceId: camera.deviceId,
                    facingMode: ["user", "environment"],
                    width: { ideal: 1280, min: 640 },
                    height: { ideal: 720, min: 480 },
                    frameRate: { ideal: 30, min: 15 },
                },
            });

            // Attach stream to video element
            videoEl.srcObject = stream;
            videoStream = stream;

            // Get video track
            const track = stream.getVideoTracks()[0];

            // Check capabilities and enable best settings
            const capabilities = track.getCapabilities();

            // Try to enable continuous autofocus for better barcode detection
            try {
                if (capabilities.focusMode && capabilities.focusMode.includes("continuous")) {
                    await track.applyConstraints({
                        advanced: [{ focusMode: "continuous" }],
                    });
                }
            } catch (e) {
                // Autofocus isn't supported, ignore
            }

            // Check torch support and update button state
            const flashlightBtn = modal.querySelector(".sc-flashlight");
            if (flashlightBtn) {
                if (capabilities.torch) {
                    flashlightBtn.classList.remove("is-disabled");
                } else {
                    flashlightBtn.classList.add("is-disabled");
                }
            }

            // Initialize the reader with hints for faster EAN/UPC scanning
            const hints = new Map();
            // Only scan for EAN-13, EAN-8, UPC-A, UPC-E (common product barcodes)
            hints.set(DecodeHintType.POSSIBLE_FORMATS, [
                BarcodeFormat.EAN_13,
                BarcodeFormat.EAN_8,
                BarcodeFormat.UPC_A,
                BarcodeFormat.UPC_E,
            ]);
            // Try harder to find barcodes
            hints.set(DecodeHintType.TRY_HARDER, true);

            codeReader = new BrowserMultiFormatReader(hints);

            // Start continuous decoding from the video element
            codeReader.decodeFromVideoElement(videoEl, (result, error) => {
                if (result) {
                    onScanSuccess(result.getText(), result);
                }
                // Ignore errors (they happen continuously while scanning)
            });

            // Wait for the video stream to be ready
            videoEl.addEventListener(
                "loadedmetadata",
                () => {
                    // Hide loading indicator
                    const loadingEl = modal.querySelector(".js-scanner-loading");
                    if (loadingEl) {
                        loadingEl.classList.add("is-hidden");
                    }

                    // Start timer to show "not found" message after 10 seconds
                    const statusEl = modal.querySelector(".sc-viewfinder__status");
                    if (statusEl) {
                        const scanningText = statusEl.dataset.scanning;
                        const notFoundText = statusEl.dataset.notFound;
                        const textEl = statusEl.querySelector(".sc-text");
                        const dotsEl = statusEl.querySelector(".sc-dots");

                        // Reset to scanning text
                        if (textEl) textEl.textContent = scanningText;
                        if (dotsEl) dotsEl.style.display = "inline-block";

                        // Set timeout to show not found message
                        scanningTimeoutId = setTimeout(() => {
                            if (textEl) textEl.textContent = notFoundText;
                            if (dotsEl) dotsEl.style.display = "none";
                        }, 10000);
                    }
                },
                { once: true }
            );

            // Create a blurred overlay effect
            if (canvas) {
                const ctx = canvas.getContext("2d");
                const frameElement = document.querySelector(".sc-viewfinder__frame");

                const drawBlurredOverlay = () => {
                    if (!videoEl.videoWidth || !videoEl.videoHeight) {
                        blurAnimationId = requestAnimationFrame(drawBlurredOverlay);
                        return;
                    }

                    // Use parent container dimensions for a perfect fit
                    const container = canvas.parentElement;
                    canvas.width = container.offsetWidth;
                    canvas.height = container.offsetHeight;

                    // Get frame size from actual element (responsive)
                    const frameWidth = frameElement ? frameElement.offsetWidth : 280;
                    const frameHeight = frameElement ? frameElement.offsetHeight : 280;
                    const frameX = (canvas.width - frameWidth) / 2;
                    const frameY = (canvas.height - frameHeight) / 2;

                    // Draw blurred video with object-fit: cover behavior
                    const videoAspect = videoEl.videoWidth / videoEl.videoHeight;
                    const canvasAspect = canvas.width / canvas.height;

                    let sx, sy, sWidth, sHeight;

                    if (videoAspect > canvasAspect) {
                        // Video is wider - fit to height, crop sides
                        sHeight = videoEl.videoHeight;
                        sWidth = videoEl.videoHeight * canvasAspect;
                        sx = (videoEl.videoWidth - sWidth) / 2;
                        sy = 0;
                    } else {
                        // Video is taller - fit to width, crop top/bottom
                        sWidth = videoEl.videoWidth;
                        sHeight = videoEl.videoWidth / canvasAspect;
                        sx = 0;
                        sy = (videoEl.videoHeight - sHeight) / 2;
                    }

                    ctx.filter = "blur(3px)";
                    ctx.drawImage(
                        videoEl,
                        sx,
                        sy,
                        sWidth,
                        sHeight,
                        0,
                        0,
                        canvas.width,
                        canvas.height
                    );

                    // Only clear the center area when actively scanning (message is hidden)
                    const messageEl = modal.querySelector(".js-ean-scanner-message");
                    if (messageEl && messageEl.classList.contains("is-hidden")) {
                        ctx.filter = "none";

                        // Create rounded rectangle path matching the frame's border-radius
                        const radius = 32;
                        ctx.save();
                        ctx.beginPath();
                        ctx.roundRect(frameX, frameY, frameWidth, frameHeight, radius);
                        ctx.clip();
                        ctx.clearRect(frameX, frameY, frameWidth, frameHeight);
                        ctx.restore();
                    }

                    blurAnimationId = requestAnimationFrame(drawBlurredOverlay);
                };

                drawBlurredOverlay();
            }
        } catch (error) {
            console.error("Failed to start scanner", error);

            // Hide loading indicator
            const loadingEl = modal.querySelector(".js-scanner-loading");
            if (loadingEl) {
                loadingEl.classList.add("is-hidden");
            }

            const msg = document.querySelector(".js-ean-scanner-message");
            if (msg) {
                msg.classList.remove("is-hidden", "is-loading", "is-success");
                msg.classList.add("is-error");

                // Provide specific error messages based on error type
                let errorMessage =
                    msg.dataset?.cameraErrorMessage || "Nepodarilo sa otvoriť kameru.";

                if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
                    // Detect if running inside Median app for better error message
                    const isMedianApp = typeof median !== "undefined";
                    errorMessage = isMedianApp
                        ? "Prístup k kamere bol zamietnutý. Povoľte kameru v nastaveniach aplikácie (Nastavenia → Aplikácie → Povolenia → Kamera)."
                        : "Prístup k kamere bol zamietnutý. Povoľte kameru v nastaveniach prehliadača.";
                } else if (
                    error.name === "NotFoundError" ||
                    error.name === "DevicesNotFoundError"
                ) {
                    errorMessage = "Nebola nájdená žiadna kamera na tomto zariadení.";
                } else if (error.name === "NotReadableError" || error.name === "TrackStartError") {
                    errorMessage =
                        "Kamera je obsadená inou aplikáciou. Zatvorte ostatné aplikácie používajúce kameru.";
                } else if (error.name === "OverconstrainedError") {
                    errorMessage = "Kamera nepodporuje požadované nastavenia.";
                } else if (error.name === "NotSupportedError") {
                    errorMessage = "Prístup ku kamere nie je podporovaný. Použite HTTPS.";
                } else if (error.name === "TypeError") {
                    errorMessage = "Chyba inicializácie kamery. Skúste obnoviť stránku.";
                }

                const textEl = msg.querySelector(".sc-message__text");
                if (textEl) textEl.textContent = errorMessage;
            }
        }
    });

    $(modal).on("hidden.bs.modal", () => {
        if (blurAnimationId) {
            cancelAnimationFrame(blurAnimationId);
            blurAnimationId = null;
        }

        // Clear scanning timeout
        if (scanningTimeoutId) {
            clearTimeout(scanningTimeoutId);
            scanningTimeoutId = null;
        }

        // Turn off flashlight and stop stream
        if (videoStream) {
            const track = videoStream.getVideoTracks()[0];

            // Turn off flashlight if on
            if (flashlightOn && track) {
                try {
                    track.applyConstraints({
                        advanced: [{ torch: false }],
                    });
                } catch (err) {
                    console.error("Failed to turn off flashlight", err);
                }
            }

            // Stop all tracks
            videoStream.getTracks().forEach((track) => track.stop());
        }

        // Reset states
        videoStream = null;
        flashlightOn = false;
        isScanning = false;
        lastScannedCode = null;
        nativeResultMode = false;
        modal.classList.remove("is-native-result");

        const flashlightBtn = modal.querySelector(".sc-flashlight");
        if (flashlightBtn) {
            flashlightBtn.classList.remove("is-active");
            flashlightBtn.classList.remove("is-disabled");
        }

        // Reset message state
        const messageEl = modal.querySelector(".js-ean-scanner-message");
        if (messageEl) {
            messageEl.classList.remove("is-loading", "is-success", "is-error");
            messageEl.classList.add("is-hidden");
            const textEl = messageEl.querySelector(".sc-message__text");
            if (textEl) textEl.textContent = "";
        }

        // Reset loading indicator for next open
        const loadingEl = modal.querySelector(".js-scanner-loading");
        if (loadingEl) {
            loadingEl.classList.remove("is-hidden");
        }
    });
}

// Define what happened with scanned code
function onScanSuccess(decodedText, decodedResult) {
    // Uncomment to see results of what camera sees
    // console.log("Decoded text:", decodedText);

    // Prevent scanning the same code repeatedly
    if (isScanning || lastScannedCode === decodedText) {
        return;
    }

    isScanning = true;
    lastScannedCode = decodedText;

    // Clear the scanning timeout since we found a code
    if (scanningTimeoutId) {
        clearTimeout(scanningTimeoutId);
        scanningTimeoutId = null;
    }

    const messageEl = document.querySelector(".js-ean-scanner-message");
    const textEl = messageEl.querySelector(".sc-message__text");
    const loadingMessage = messageEl.dataset.loadingMessage;
    const successMessage = messageEl.dataset.successMessage;
    const errorMessage = messageEl.dataset.successError;

    // Show loading state
    messageEl.classList.remove("is-hidden", "is-success", "is-error");
    messageEl.classList.add("is-loading");
    if (textEl) textEl.textContent = loadingMessage || "Loading...";

    client
        .post("/vyhladavanie-ean", { ean_code: decodedText })
        .then((response) => {
            if (messageEl) {
                messageEl.classList.remove("is-loading");

                if (response?.status && response.data?.product?.link) {
                    messageEl.classList.add("is-success");
                    if (textEl) textEl.textContent = successMessage;

                    // Delay redirect to show a success message
                    setTimeout(() => {
                        window.location.href = response.data.product.link;
                    }, 1500);
                } else {
                    messageEl.classList.add("is-error");
                    if (textEl) textEl.textContent = errorMessage;
                    isScanning = false;
                }
            }
        })
        .catch((error) => {
            if (messageEl) {
                messageEl.classList.remove("is-loading");
                messageEl.classList.add("is-error");
                if (textEl) textEl.textContent = errorMessage;
            }
            isScanning = false;
            console.error(error);
        });
}

// Manual input functionality
if (modal) {
    const keyboardBtn = modal.querySelector(".js-scanner-keyboard");
    const manualInputPopup = modal.querySelector(".js-ean-manual-input");
    const manualInputClose = modal.querySelector(".js-manual-input-close");
    const manualInputForm = modal.querySelector(".js-manual-input-form");
    const manualInputField = modal.querySelector(".js-manual-input-field");

    // Show manual input popup
    if (keyboardBtn && manualInputPopup) {
        keyboardBtn.addEventListener("click", () => {
            manualInputPopup.classList.remove("is-hidden");
            // Focus input field
            setTimeout(() => {
                if (manualInputField) manualInputField.focus();
            }, 100);
        });
    }

    // Close manual input popup
    if (manualInputClose && manualInputPopup) {
        manualInputClose.addEventListener("click", () => {
            manualInputPopup.classList.add("is-hidden");
            if (manualInputField) manualInputField.value = "";
        });
    }

    // Handle form submission
    if (manualInputForm) {
        manualInputForm.addEventListener("submit", (e) => {
            e.preventDefault();

            const code = manualInputField.value.trim();
            if (!code) {
                return;
            }

            // Hide manual input popup
            manualInputPopup.classList.add("is-hidden");
            manualInputField.value = "";

            // Process the manually entered code using the same logic
            onScanSuccess(code, null);
        });
    }

    // Image scanner functionality
    const imageBtn = modal.querySelector(".js-scanner-image");
    const imageInput = modal.querySelector(".js-scanner-image-input");

    // Trigger file input when image button clicked
    if (imageBtn && imageInput) {
        imageBtn.addEventListener("click", () => {
            imageInput.click();
        });
    }

    // Handle image selection
    if (imageInput) {
        imageInput.addEventListener("change", async (e) => {
            const file = e.target.files[0];
            if (!file) {
                return;
            }

            try {
                // Show loading message
                const messageEl = modal.querySelector(".js-ean-scanner-message");
                const textEl = messageEl.querySelector(".sc-message__text");
                if (messageEl) {
                    messageEl.classList.remove("is-hidden", "is-success", "is-error");
                    messageEl.classList.add("is-loading");
                    if (textEl) textEl.textContent = "Spracúvam obrázok...";
                }

                // Create image element
                const img = new Image();
                const imageUrl = URL.createObjectURL(file);

                img.onload = async () => {
                    try {
                        // Use ZXing to decode barcode from image
                        const reader = new BrowserMultiFormatReader();
                        const result = await reader.decodeFromImageElement(img);

                        // Clean up
                        URL.revokeObjectURL(imageUrl);
                        imageInput.value = "";

                        // Process the scanned code
                        onScanSuccess(result.getText(), result);
                    } catch (error) {
                        console.error("Failed to decode barcode from image:", error);

                        // Clean up
                        URL.revokeObjectURL(imageUrl);
                        imageInput.value = "";

                        // Show error message
                        if (messageEl) {
                            messageEl.classList.remove("is-loading");
                            messageEl.classList.add("is-error");
                            if (textEl) {
                                textEl.textContent = "Nepodarilo sa nájsť čiarový kód na obrázku.";
                            }
                        }

                        // Reset scanning flag
                        isScanning = false;
                    }
                };

                img.onerror = () => {
                    console.error("Failed to load image");
                    URL.revokeObjectURL(imageUrl);
                    imageInput.value = "";

                    // Show error message
                    if (messageEl) {
                        messageEl.classList.remove("is-loading");
                        messageEl.classList.add("is-error");
                        if (textEl) {
                            textEl.textContent = "Nepodarilo sa načítať obrázok.";
                        }
                    }

                    isScanning = false;
                };

                img.src = imageUrl;
            } catch (error) {
                console.error("Error processing image:", error);
                imageInput.value = "";
                isScanning = false;
            }
        });
    }
}

// API pre natívny scanner v appke (camera-scanner-app.js). Zámerne cez window
// namiesto ES exportu — app modul nesmie ťahať ZXing/jQuery závislosti tohto
// súboru (kvôli legacy-coexistence testu).
if (modal) {
    const resultRetryBtn = modal.querySelector(".sc-message__retry");
    const resultRetryDefaultLabel = resultRetryBtn ? resultRetryBtn.textContent : "";

    window.__eanScannerUi = {
        // Spracuje naskenovaný kód (POST /vyhladavanie-ean + redirect/error message).
        // Dedup guard sa resetuje — v result mode nebeží kontinuálne dekódovanie,
        // takže opakovaný sken toho istého kódu je legitímny (retry).
        processScannedCode: (code) => {
            isScanning = false;
            lastScannedCode = null;
            onScanSuccess(code, null);
        },

        // Otvorí modal v result mode — bez kamery a scanner chrome (CSS
        // .is-native-result), len message overlay. No-op, ak už je otvorený.
        openResultModal: () => {
            nativeResultMode = true;
            modal.classList.add("is-native-result");
            if (resultRetryBtn) {
                resultRetryBtn.textContent = resultRetryDefaultLabel;
            }
            $(modal).modal("show");
        },

        // Zobrazí error message s vlastným textom (permissionDenied/unavailable)
        showResultError: (text, retryLabel) => {
            const messageEl = modal.querySelector(".js-ean-scanner-message");
            if (!messageEl) {
                return;
            }
            messageEl.classList.remove("is-hidden", "is-loading", "is-success");
            messageEl.classList.add("is-error");
            const textEl = messageEl.querySelector(".sc-message__text");
            if (textEl) textEl.textContent = text;
            if (retryLabel && resultRetryBtn) {
                resultRetryBtn.textContent = retryLabel;
            }
        },

        // Retry tlačidlo v result mode spustí tento handler namiesto resetu kamery
        setRetryHandler: (fn) => {
            nativeRetryHandler = fn;
        },

        // Zavrie modal (pred opakovaným natívnym skenom)
        closeModal: () => {
            $(modal).modal("hide");
        },
    };
}
